import { useQuery } from '@tanstack/react-query';
import ms from 'ms';

import { toast } from '@hivebrite/design-system/Toast';

import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import { dynamicTopicResourcePath } from 'hb-react/shared/apps/Topics/utils';
import {
  networkPagesIdentifiersFeaturesMap,
  networkToTopicIdentifiersMap,
} from 'hb-react/shared/constants/pageIdentifiers';
import { type PageIdentifier } from 'hb-react/shared/constants/pageIdentifiers';
import { axiosInstance } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import { type AppPageConfig } from 'hb-react/types';

const getPageConfigPath = ({
  identifier,
  topicId,
  whitelabelURL,
  pageLocale,
}) => {
  if (topicId) {
    const topicPageIdentifier = networkToTopicIdentifiersMap[identifier];
    const isNativePage = Boolean(topicPageIdentifier);

    return dynamicTopicResourcePath(
      Routes.topic_page_path(topicId, topicPageIdentifier || identifier, {
        format: 'json',
        page_locale: pageLocale,
        native_page: isNativePage,
      }),
      {
        whitelabelURL,
        topicId,
      },
    );
  }

  return Routes.network_page_customizable_json_path(identifier, {
    native_page: Boolean(networkPagesIdentifiersFeaturesMap[identifier]),
    page_locale: pageLocale,
  });
};

interface Props {
  enabled?: boolean;
  identifier: PageIdentifier | string;
  initialData?: AppPageConfig;
  topicId?: string | null;
  whitelabelURL?: string;
}

export const usePageConfigQuery = ({
  enabled = true,
  identifier,
  initialData,
  topicId = null,
  whitelabelURL,
}: Props) => {
  const pageLocale = I18n.locale;

  return useQuery({
    queryKey: ['pageConfig', topicId, identifier, pageLocale],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get<{
          page_customizable: AppPageConfig;
        }>(
          getPageConfigPath({
            identifier,
            topicId,
            whitelabelURL,
            pageLocale,
          }),
        );

        return response.data.page_customizable;
      } catch (error) {
        if (error?.response?.status === 403) {
          displayXhrError(error);
        } else {
          toast.error(__('Sorry, an error occurred'));
        }

        errorReporting.captureException(error);

        throw error;
      }
    },
    enabled: !!identifier && enabled,
    gcTime: Infinity,
    initialData,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: ms('15m'),
  });
};
